import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, Sliders, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { readConsent, writeConsent } from "@/utils/consent";
import { loadPixelIfConsented } from "@/utils/pixel";

const TID = {
  banner: "cookie-banner",
  acceptAll: "cookie-accept-all",
  rejectAll: "cookie-reject-all",
  customize: "cookie-customize",
  prefsDialog: "cookie-prefs-dialog",
  marketingToggle: "cookie-marketing-toggle",
  savePrefs: "cookie-save-prefs",
};

export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (!c.decided) {
      setOpen(true);
    } else if (c.marketing) {
      // re-arm pixel on every load if user previously consented
      loadPixelIfConsented();
    }

    const onChange = () => {
      const next = readConsent();
      if (next.decided) setOpen(false);
      if (next.marketing) loadPixelIfConsented();
    };
    window.addEventListener("np-consent-changed", onChange);
    return () => window.removeEventListener("np-consent-changed", onChange);
  }, []);

  const acceptAll = () => {
    writeConsent({ marketing: true });
    loadPixelIfConsented();
    setOpen(false);
  };
  const rejectAll = () => {
    writeConsent({ marketing: false });
    setOpen(false);
  };
  const openPrefs = () => {
    const c = readConsent();
    setMarketing(c.marketing);
    setPrefsOpen(true);
  };
  const savePrefs = () => {
    writeConsent({ marketing });
    if (marketing) loadPixelIfConsented();
    setPrefsOpen(false);
    setOpen(false);
  };

  if (!open && !prefsOpen) return null;

  return (
    <>
      {open && !prefsOpen && (
        <div
          data-testid={TID.banner}
          role="dialog"
          aria-label="Banner consenso cookie"
          className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6 animate-[fadeUp_0.4s_ease]"
        >
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(26,42,58,0.45)] overflow-hidden border border-[#e3eaf2]">
            <div className="h-1.5 bg-tension w-full" />
            <div className="p-6 md:p-7 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-5 md:gap-7 items-start md:items-center">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "var(--np-quaternary)" }}
              >
                <Cookie size={22} style={{ color: "var(--np-secondary)" }} />
              </div>
              <div className="min-w-0">
                <p
                  className="text-base md:text-lg leading-snug"
                  style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
                >
                  Rispettiamo la tua privacy
                </p>
                <p className="text-sm md:text-[15px] leading-relaxed mt-1.5" style={{ color: "var(--np-muted)" }}>
                  Usiamo cookie tecnici necessari al funzionamento del sito e, previo consenso, cookie di analisi
                  statistica (Google Analytics 4) e di marketing (Meta Pixel, Google Ads) per misurare le campagne
                  pubblicitarie. Puoi accettarli tutti, rifiutarli o personalizzare la tua scelta. Leggi la{" "}
                  <Link
                    to="/cookie-policy"
                    className="underline-offset-4 hover:underline font-medium"
                    style={{ color: "var(--np-primary)" }}
                  >
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5 md:gap-3 md:justify-end">
                <button
                  type="button"
                  data-testid={TID.rejectAll}
                  onClick={rejectAll}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold border transition hover:bg-[#f5f8fb]"
                  style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}
                >
                  Rifiuta
                </button>
                <button
                  type="button"
                  data-testid={TID.customize}
                  onClick={openPrefs}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold border transition inline-flex items-center gap-1.5 hover:bg-[#f5f8fb]"
                  style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}
                >
                  <Sliders size={14} />
                  Personalizza
                </button>
                <button
                  type="button"
                  data-testid={TID.acceptAll}
                  onClick={acceptAll}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold text-white shadow-md transition hover:brightness-110"
                  style={{ background: "var(--np-secondary)" }}
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {prefsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Preferenze cookie"
          data-testid={TID.prefsDialog}
          className="fixed inset-0 z-[110] flex items-end md:items-center justify-center p-4 md:p-6"
          style={{ background: "rgba(15, 26, 38, 0.55)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPrefsOpen(false);
          }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#e3eaf2]">
            <div className="h-1.5 bg-tension w-full" />
            <div className="p-7 md:p-8">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2
                    className="text-xl md:text-2xl"
                    style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}
                  >
                    Preferenze cookie
                  </h2>
                  <p className="text-sm mt-1" style={{ color: "var(--np-muted)" }}>
                    Scegli quali categorie di cookie vuoi attivare.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPrefsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-[#f0f4f8] transition"
                  aria-label="Chiudi"
                >
                  <X size={18} style={{ color: "var(--np-muted)" }} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-[#e3eaf2] p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} style={{ color: "var(--np-secondary)" }} />
                      <p className="font-semibold" style={{ color: "var(--np-primary)" }}>
                        Necessari
                      </p>
                    </div>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--np-muted)" }}>
                      Indispensabili per il corretto funzionamento del sito. Sempre attivi.
                    </p>
                  </div>
                  <Switch checked disabled aria-label="Cookie necessari (sempre attivi)" />
                </div>

                <div className="rounded-xl border border-[#e3eaf2] p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="marketing" className="cursor-pointer">
                      <p className="font-semibold" style={{ color: "var(--np-primary)" }}>
                        Marketing
                      </p>
                    </Label>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--np-muted)" }}>
                      Meta Pixel di Facebook/Instagram per misurare l&apos;efficacia delle campagne pubblicitarie e
                      mostrare annunci pertinenti.
                    </p>
                  </div>
                  <Switch
                    id="marketing"
                    data-testid={TID.marketingToggle}
                    checked={marketing}
                    onCheckedChange={setMarketing}
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setPrefsOpen(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold border transition hover:bg-[#f5f8fb]"
                  style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  data-testid={TID.savePrefs}
                  onClick={savePrefs}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-md transition hover:brightness-110"
                  style={{ background: "var(--np-secondary)" }}
                >
                  Salva preferenze
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform: translateY(20px);} to {opacity:1; transform: translateY(0);} }`}</style>
    </>
  );
}
